import { ArrowUpRight, BookOpen } from "lucide-react";
import { SITE } from "@/config/site";
import { ChapterHeading } from "@/components/ChapterHeading";
import { Reveal } from "@/components/Reveal";
import { StarOrnament, StarPattern } from "@/components/Ornament";
import { openBooking, trackEvent, trackOutbound, withUtm } from "@/lib/analytics";
import "@/styles/theme-v2.css";

// ============================================================================
// PERFORMERS V2 — identical copy and honesty guardrails as the live
// Performers.jsx (still one verified artist, still "full lineup to be
// revealed" — nothing invented). The elevation is purely in the frame: a
// slow Ken Burns drift on the portrait and a jewel-case inner hairline to
// match the same treatment now used in HeroV2's poster frame, so the two
// portrait treatments feel like one considered system rather than two
// different components that happen to share a border color.
// ============================================================================

export function PerformersV2() {
  const artist = SITE.artist;
  // Single verified, hard credibility number pulled from the same source of
  // truth as the Artist page's stat strip — no new figure invented here.
  const booksStat = artist.stats?.find((s) => s.label === "Published Books");

  return (
    <section id="artists" data-testid="performers-section" className="relative px-5 py-20 sm:px-8 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-6xl">
        <ChapterHeading
          number="03"
          title="The Artist Behind Chitti Ayee Hai"
          urdu="فنکار"
          testId="performers-heading"
        />

        <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center lg:gap-16">
          {/* Portrait */}
          <Reveal className="lg:col-span-5">
            <div className="relative mx-auto max-w-[340px] lg:mx-0">
              <div className="arch-frame relative aspect-[3/4.2] w-full overflow-hidden border border-[#D4AF37]/35 bg-[#0E0C0A]">
                {artist.hasPhoto && artist.photo ? (
                  <img
                    src={artist.photo}
                    alt={`${artist.fullName} — portrait`}
                    data-testid="performer-artist-photo"
                    className="cah-kenburns absolute inset-0 h-full w-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : null}
                <div className="pointer-events-none absolute inset-0 -z-[1] flex flex-col items-center justify-center bg-[#0E0C0A]">
                  <StarPattern id="performer-arch-pattern" opacity={0.3} />
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_22%,rgba(212,175,55,0.2),transparent_65%)]" />
                  <p
                    dir="rtl"
                    lang="ur"
                    className="font-urdu relative text-5xl leading-relaxed text-[#E6CA65]"
                    style={{ textShadow: "0 0 44px rgba(212,175,55,0.45)" }}
                  >
                    {artist.urdu}
                  </p>
                </div>
                {/* new: inner gold hairline inset — the same "jewel case" touch
                    added to the hero poster frame */}
                <div className="pointer-events-none absolute inset-[8px] border border-[#F3E5AB]/15" aria-hidden="true" />
              </div>
              <div
                className="arch-frame absolute -left-4 top-4 -z-10 h-full w-full border border-[#D4AF37]/15"
                aria-hidden="true"
              />
            </div>
          </Reveal>

          {/* Copy */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37]">
                Also credited professionally as {artist.stageName}
              </p>
              <h3 className="font-display mt-3 text-3xl text-[#F7F4EF] sm:text-4xl">{artist.fullName}</h3>
              <p className="font-accent mt-3 text-lg italic text-[#C8C2B7] sm:text-xl">{artist.tagline}</p>
            </Reveal>

            <Reveal delay={0.16}>
              <div className="mt-6 flex flex-wrap items-center gap-2" aria-label="Artistic roles">
                {artist.roles.map((role) => (
                  <span
                    key={role}
                    className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#F3E5AB]"
                  >
                    {role}
                  </span>
                ))}
                {booksStat && (
                  <span
                    data-testid="performer-books-credibility-chip"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/[0.09] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#E6CA65]"
                  >
                    <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
                    {booksStat.value} Published Books
                  </span>
                )}
              </div>
            </Reveal>

            <Reveal delay={0.22}>
              <p className="mt-7 max-w-2xl text-base leading-[1.8] text-[#C8C2B7] sm:text-lg">{artist.intro}</p>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <a
                  href="/artist"
                  data-testid="performer-read-story-btn"
                  onClick={() => trackEvent("artist_profile_click", { cta_location: "homepage_teaser" })}
                  className="btn-gold inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full px-8 text-sm font-bold uppercase tracking-[0.12em] text-[#0A0805] transition-[transform,box-shadow,background-position] duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E6CA65] sm:text-base"
                >
                  Read His Full Story
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
                <a
                  href={withUtm(artist.website)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="performer-official-site-link"
                  onClick={() => {
                    trackEvent("performer_website_click", { performer: artist.fullName });
                    trackOutbound("performer-website", { performer: artist.fullName });
                  }}
                  className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 px-7 text-sm font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] transition-colors duration-300 hover:bg-[#D4AF37]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E6CA65] sm:text-base"
                >
                  {artist.websiteLabel}
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.36}>
              <button
                type="button"
                data-testid="performer-tickets-live-link"
                onClick={() => openBooking("performers_teaser")}
                className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#918A7E] underline decoration-[#D4AF37]/30 underline-offset-4 transition-colors hover:text-[#F3E5AB]"
              >
                Tickets Are Live
                <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="performer-card-more"
            className="mt-14 flex flex-col items-center gap-3 rounded-3xl border border-dashed border-[#D4AF37]/25 px-8 py-8 text-center"
          >
            <StarOrnament className="h-6 w-6 text-[#D4AF37]/40" />
            <p className="text-sm leading-relaxed text-[#918A7E] sm:text-base">
              The full lineup will be revealed by the organizers — follow our social pages for the reveal.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
